import { quoteRequestInputSchema } from '@repo/api-contracts';
import { useState } from 'react';
import { trpc } from '@/shared/api';
import {
  type QuoteRequestCommonValues,
  type QuoteRequestDestinationErrors,
  type QuoteRequestFieldErrors,
  type QuoteRequestSubmissionErrors,
  quoteRequestSubmitErrors,
  realClock,
  toQuoteRequestCommonInput,
  type UseSubmitQuoteRequestOptions,
  type UseSubmitQuoteRequestResult,
  validateQuoteRequest,
} from './quote-request-submission';

export type {
  MemberDestinationValues,
  QuoteRequestDestinationErrors,
  QuoteRequestFieldErrors,
  QuoteRequestFieldKey,
  ShippingAddressValues,
} from './quote-request-submission';

/** 검증 전 폼 입력 — 수량·날짜도 입력창 문자열 그대로 받고 검증은 safeParse 가 한다. 견적 수량은 배송지 수량 합계다. */
export interface QuoteRequestFormValues extends QuoteRequestCommonValues {
  readonly designId: string;
  readonly printMethodId: string;
}

/** 입력창 문자열을 계약 입력 모양으로 — 디자인·인쇄 방식 + 공용 칸. */
const toInput = (values: QuoteRequestFormValues): unknown => ({
  designId: values.designId,
  printMethodId: values.printMethodId,
  ...toQuoteRequestCommonInput(values),
});

/**
 * 견적 요청 제출(quotes.submitRequest, 견적 설계 §5-4·§5-6, SUPER-86 spec §5-1).
 * 계약 스키마와 서버와 같은 규칙(합계 최소 수량·견적 금액 상한·한국 내일 이후)으로 선검증 → 공용 칸 오류와 카드 key 별 칸 오류.
 * 서버 거절(준비 중·요청 불가 사유·수량·입력 오류)은 서버 문구를 폼 오류로 보여 준다 — 오류 형태에 도메인 코드가 없어
 * 사유는 문구로만 온다(최종 리뷰 T6 ruling).
 */
export const useSubmitQuoteRequest = ({
  minOrderQty,
  priceTiers,
  shippingFeeSettings,
  sizeOptions,
  now = realClock,
}: UseSubmitQuoteRequestOptions): UseSubmitQuoteRequestResult<QuoteRequestFormValues> => {
  const [fieldErrors, setFieldErrors] = useState<QuoteRequestFieldErrors>({});
  const [destinationErrors, setDestinationErrors] = useState<QuoteRequestDestinationErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const mutation = trpc.quotes.submitRequest.useMutation();

  const show = (errors: QuoteRequestSubmissionErrors): void => {
    setFieldErrors(errors.fieldErrors);
    setDestinationErrors(errors.destinationErrors);
    setFormError(errors.formError);
  };

  const submit = async (values: QuoteRequestFormValues): Promise<string | null> => {
    const validation = validateQuoteRequest(
      quoteRequestInputSchema.safeParse(toInput(values)),
      values,
      { minOrderQty, priceTiers, now: now() },
      shippingFeeSettings,
      undefined,
      sizeOptions,
    );
    show(validation);

    if (validation.input === null) {
      return null;
    }

    try {
      const result = await mutation.mutateAsync(validation.input);
      return result.quoteRequestId;
    } catch (error) {
      show(quoteRequestSubmitErrors(error, values));
      return null;
    }
  };

  return { fieldErrors, destinationErrors, formError, isPending: mutation.isPending, submit };
};
